/**
 * ONE TAP — the particle system.
 *
 * One GPU-simulated, pooled, allocation-free particle engine that every other effect in
 * the game draws from. Two draw calls total: one additive batch (light: sparks, flashes,
 * ignition, snap rings) and one matte batch (ink: chips, dust, drips, shards).
 *
 * THE IMPORTANT IDEA: particles are never simulated on the CPU. A particle's whole life
 * is an analytic function of `t = uTime - birth`, evaluated in the vertex shader:
 *
 *     v(t) = (v0 + g/k) e^(-kt) - g/k
 *     p(t) = p0 + (v0 + g/k)(1 - e^(-kt))/k - (g/k) t
 *
 * — the exact solution for gravity plus linear drag. So the CPU touches a particle exactly
 * once, on spawn, and never again. Death is free: the vertex shader clips any instance
 * whose `t` is outside `[0, life)` to behind the far plane, so an expired particle costs
 * one vertex invocation and zero fragments. That is what makes thousands of them safe.
 *
 * STYLE: nothing here is a soft round blob. Every shape is a hard-edged graphic form —
 * ink splats, chipped triangles, comic stars, hollow rings, screen-toned dust puffs —
 * antialiased with `fwidth` so the edge stays a *drawn line* at any distance, never a
 * gradient. The matte batch additionally draws a real ink outline around each particle,
 * held constant IN PIXELS exactly like the inverted-hull outlines on the world geometry,
 * so it is a hairline at arm's length and still a hairline across the arena.
 *
 * THE BRIGHT-WORLD RULE, which decides which batch an effect belongs in:
 *
 *   ONE TAP is played on white architecture under a blue sky. Additive light is a
 *   *contrast* operation, and against a surface that is already near the top of the
 *   tone curve it has nothing left to add — an additive white puff on a white wall is
 *   invisible, and ACES flattens what little survives. So:
 *
 *     - the READABLE body of an effect is always MATTE: a saturated, opaque, hard-edged
 *       shape with an ink outline. That reads against white plaster, blue sky and glass
 *       equally well, because it works by hue and by value, not by luminance headroom;
 *     - ADDITIVE is reserved for a small incandescent core sitting ON TOP of a matte
 *       body, which is the same rule DESIGN.md 4 states for the gun itself: an emissive
 *       needs a dark body to read against.
 *
 *   Consequently the matte batch is the big one here and the additive batch is the
 *   garnish, which is the exact inverse of how a dark-game particle system is tuned.
 *
 * ALPHA IS NOT COVERAGE HERE. The scene buffer's alpha channel is the selective-bloom
 * mask (`mask = 1 - alpha`, see render/toon.ts and render/postfx.ts), so these materials
 * use premultiplied colour and hand-tuned separate alpha blending:
 *
 *   - matte batch: alpha does `src + dst(1-src)`, which leaves an untouched dst of 1.0 at
 *     1.0 — matte ink never blooms, by construction.
 *   - additive batch: alpha does `dst - src` (reverse subtract), so a particle asking for
 *     `bloom = 0.9` pulls the mask down to 0.1 and blooms, and a particle asking for 0
 *     leaves the mask alone. A fading particle's bloom fades with it.
 *
 * Client-only: touches `three`. Never import from src/shared.
 */

import * as THREE from 'three';
import { PALETTE } from '@shared/constants';
import { makeRng } from '@shared/math';

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

/** Wobbly hard-edged ink splat. The house shape — blood, paint, impact cores. */
export const P_INK = 0;
/** Chipped slab with a broken corner. Concrete debris, wall spall. */
export const P_CHIP = 1;
/** Velocity-aligned streak with a hot core. Sparks, tracers, speed lines. */
export const P_SPARK = 2;
/** Hollow ring. Shock fronts, snap rings, muzzle blast collars. */
export const P_RING = 3;
/** Four-point comic star. Impacts, ignition, headshot confirmation. */
export const P_STAR = 4;
/** Cloud silhouette with screen-tone dots. Dust, dry-fire puff, smoke. */
export const P_PUFF = 5;
/** Hard square. Screen-tone specks, glass glints, grit. */
export const P_DOT = 6;
/** Triangular shard. Glass, neon tube, metal splinter. */
export const P_SHARD = 7;

export type BlendKind = 'add' | 'over';

// ---------------------------------------------------------------------------
// The VFX colour contract
// ---------------------------------------------------------------------------

/**
 * Every colour any effect in this game is allowed to use, and nothing else.
 *
 * DESIGN.md 4: "colour means something because there is so little of it". A frame with
 * jade grapple sparks, sun-yellow heal motes, pink blood and cyan glass chips in it has
 * spent all of its colour on nothing, and the one colour that actually carries
 * information — amber for a chambered round — stops being a signal.
 *
 * So the whole effect vocabulary is four hues:
 *
 *   TEAM     whose action this was
 *   AMBER    the chamber, and only the chamber
 *   RED      damage and danger, and only damage and danger
 *   INK      everything that is merely physics: debris, dust, smoke, scuffs, outlines
 *
 * plus `core`, an incandescent white that is never allowed to be the readable shape —
 * it only ever sits inside a matte body as the part that blooms.
 *
 * These deliberately re-export from PALETTE rather than restating hex, so the look can
 * never drift between the renderer and the VFX (see @shared/constants).
 */
export const VFX = {
  /** Chambered. The only warm accent in the effect vocabulary; blooms. */
  hot: PALETTE.loaded,
  /** Damage, blood, danger. */
  danger: PALETTE.danger,
  /** The tone blood dries to. Dark enough to read as ink on white plaster. */
  dangerDeep: 0x7c1420,
  /** Outline colour and the tone all debris settles to. Soft slate, never black. */
  ink: PALETTE.ink,
  /** A shade between `ink` and `dust` — solid debris, spall, chips. */
  debris: 0x77839a,
  /** Dust and smoke. The cool lifted shadow band; never a warm tan. */
  dust: PALETTE.shadow,
  /** What dust fades toward as it thins out. */
  dustDeep: 0x7e8ca3,
  /** Spent, inert, empty. */
  spent: PALETTE.empty,
  /** Incandescent core. ADDITIVE ONLY, and only on top of a matte body. */
  core: PALETTE.neutral,
} as const;

// ---------------------------------------------------------------------------
// Blending
// ---------------------------------------------------------------------------

/**
 * The single place the game decides how a VFX material talks to the scene buffer.
 *
 * `'add'`  — premultiplied additive colour; alpha REVERSE-SUBTRACTS, so the fragment's
 *            alpha is read as "how much this pixel should bloom".
 * `'over'` — premultiplied source-over; alpha accumulates the ordinary way and therefore
 *            leaves the bloom mask at its cleared value. Matte ink never glows.
 *
 * Both modes expect the shader to output colour ALREADY multiplied by coverage.
 */
export function applyVfxBlend(m: THREE.Material, kind: BlendKind): void {
  m.transparent = true;
  m.depthWrite = false;
  m.blending = THREE.CustomBlending;
  m.blendEquation = THREE.AddEquation;
  m.blendSrc = THREE.OneFactor;
  if (kind === 'add') {
    m.blendDst = THREE.OneFactor;
    m.blendEquationAlpha = THREE.ReverseSubtractEquation;
    m.blendSrcAlpha = THREE.OneFactor;
    m.blendDstAlpha = THREE.OneFactor;
  } else {
    m.blendDst = THREE.OneMinusSrcAlphaFactor;
    m.blendEquationAlpha = THREE.AddEquation;
    m.blendSrcAlpha = THREE.OneFactor;
    m.blendDstAlpha = THREE.OneMinusSrcAlphaFactor;
  }
}

// ---------------------------------------------------------------------------
// Colour
// ---------------------------------------------------------------------------

const _col = new THREE.Color();

/** sRGB hex -> linear working space, scaled by `intensity`. Written straight into an attribute. */
function writeColor(out: Float32Array, base: number, hex: number, intensity: number): void {
  _col.setHex(hex, THREE.SRGBColorSpace);
  out[base] = _col.r * intensity;
  out[base + 1] = _col.g * intensity;
  out[base + 2] = _col.b * intensity;
}

// ---------------------------------------------------------------------------
// GLSL
// ---------------------------------------------------------------------------

const PARTICLE_VERT = /* glsl */ `
attribute vec4 aP0;   // p0.xyz, birth
attribute vec4 aV0;   // v0.xyz, life
attribute vec4 aSD;   // size0, size1, drag, gravity
attribute vec4 aC0;   // colour0.rgb, spin0
attribute vec4 aC1;   // colour1.rgb, spinRate
attribute vec4 aX;    // shape, seed, bloom, stretch

uniform float uTime;
uniform float uFogNear;
uniform float uFogFar;
uniform float uViewportH;
uniform float uOutlinePx;

varying vec2 vP;
varying vec3 vCol;
varying float vAlpha;
varying float vShape;
varying float vSeed;
varying float vBloom;
varying float vFog;
varying float vGrow;

void main() {

	float life = max( aV0.w, 1e-4 );
	float t = uTime - aP0.w;

	// Dead or not yet born: clip the whole quad behind the far plane. Six vertex
	// invocations, zero fragments, zero CPU work. This is what makes the pool free.
	if ( t < 0.0 || t >= life ) {

		vP = vec2( 0.0 );
		vCol = vec3( 0.0 );
		vAlpha = 0.0;
		vShape = 0.0;
		vSeed = 0.0;
		vBloom = 0.0;
		vFog = 0.0;
		vGrow = 0.0;
		gl_Position = vec4( 0.0, 0.0, 2.0, 1.0 );
		return;

	}

	float u = t / life;

	// --- closed-form ballistics with linear drag
	float k = max( aSD.z, 0.02 );
	float invK = 1.0 / k;
	float e = exp( - k * t );
	vec3 gk = vec3( 0.0, aSD.w * invK, 0.0 );
	vec3 term = aV0.xyz + gk;
	vec3 wpos = aP0.xyz + term * ( ( 1.0 - e ) * invK ) - gk * t;
	vec3 wvel = term * e - gk;

	vec4 mv = modelViewMatrix * vec4( wpos, 1.0 );

	// --- billboard frame: spun by default, velocity-aligned when stretched
	float size = mix( aSD.x, aSD.y, u );
	float ang = aC0.w + aC1.w * t;
	vec2 ax = vec2( cos( ang ), sin( ang ) );
	vec2 ay = vec2( - ax.y, ax.x );
	float lengthen = 1.0;

	if ( aX.w > 0.0 ) {

		vec3 vv = ( modelViewMatrix * vec4( wvel, 0.0 ) ).xyz;
		float sp = length( vv.xy );
		if ( sp > 1e-4 ) {
			ay = vv.xy / sp;
			ax = vec2( ay.y, - ay.x );
			lengthen = 1.0 + aX.w * min( sp * 0.06, 8.0 );
		}

	}

	vec2 corner = position.xy * 2.0;
	mv.xy += ( ax * corner.x + ay * corner.y * lengthen ) * ( size * 0.5 );

	vP = corner;
	vCol = mix( aC0.xyz, aC1.xyz, u );
	// Hold full opacity almost the whole life, then leave in a hurry. Effects that should
	// deflate instead of fade drive size1 -> 0 and never reach this term.
	vAlpha = 1.0 - smoothstep( 0.78, 1.0, u );
	vShape = aX.x;
	vSeed = aX.y;
	vBloom = aX.z;
	vFog = clamp( ( - mv.z - uFogNear ) / max( uFogFar - uFogNear, 1e-3 ), 0.0, 1.0 );

	// Ink outline width, held constant in PIXELS rather than in the particle's own
	// space. Without this a 4 m shock ring wears a 30 cm marker-pen border while a
	// 5 cm chip has no visible outline at all. vP spans -1..1 across a quad whose
	// half-extent is size*0.5, so one vP unit is that many metres on screen.
	float halfPx = max( size * 0.5 * projectionMatrix[ 1 ][ 1 ] * 0.5 * uViewportH / max( - mv.z, 0.05 ), 1.0 );
	vGrow = clamp( uOutlinePx / halfPx, 0.012, 0.42 );

	gl_Position = projectionMatrix * mv;

}
`;

const PARTICLE_FRAG = /* glsl */ `
uniform vec3 uInk;
uniform vec3 uFogColor;
uniform float uPixelRatio;

varying vec2 vP;
varying vec3 vCol;
varying float vAlpha;
varying float vShape;
varying float vSeed;
varying float vBloom;
varying float vFog;
varying float vGrow;

/**
 * Every shape is an implicit form thresholded with an fwidth-wide smoothstep: hard edge,
 * no aliasing, no gradient. "grow" pushes the edge outward, which is how the matte batch
 * gets a real ink outline for one extra evaluation.
 */
float shapeMask( float shape, vec2 p, float seed, float grow ) {

	float r = length( p );
	float a = atan( p.y, p.x );

	if ( shape < 0.5 ) {

		// INK — wobbly splat. All harmonics are integer multiples of the angle, so the
		// silhouette is continuous across the atan seam.
		float rr = 0.66
			+ 0.16 * sin( a * 5.0 + seed * 11.0 )
			+ 0.09 * sin( a * 9.0 - seed * 7.0 )
			+ 0.05 * sin( a * 17.0 + seed * 3.0 )
			+ grow;
		float w = fwidth( r ) + 0.004;
		return 1.0 - smoothstep( rr - w, rr + w, r );

	} else if ( shape < 1.5 ) {

		// CHIP — a slab with one corner knocked off.
		vec2 q = abs( p );
		float slab = max( q.x * ( 1.15 + 0.85 * fract( seed * 3.137 ) ), q.y );
		float bevel = ( p.x * 0.7071 + p.y * 0.7071 ) + 0.42 * fract( seed * 7.71 );
		float d = max( slab, bevel );
		float w = fwidth( d ) + 0.006;
		float edge = 0.74 + grow;
		return 1.0 - smoothstep( edge - w, edge + w, d );

	} else if ( shape < 2.5 ) {

		// SPARK — capsule along the local +Y (which the vertex stage aligns to velocity).
		float d = length( vec2( p.x * 4.0, max( abs( p.y ) - 0.60, 0.0 ) ) );
		float w = fwidth( d ) + 0.012;
		float edge = 0.30 + grow;
		return 1.0 - smoothstep( edge - w, edge + w, d );

	} else if ( shape < 3.5 ) {

		// RING — hollow, so it reads as a shock front and never as a ball of light.
		float d = abs( r - 0.76 );
		float w = fwidth( r ) + 0.006;
		float edge = 0.085 + grow;
		return 1.0 - smoothstep( edge - w, edge + w, d );

	} else if ( shape < 4.5 ) {

		// STAR — four-point comic burst.
		float rad = 0.14 + 0.86 * pow( abs( cos( 2.0 * a ) ), 1.7 ) + grow;
		float w = fwidth( r ) + 0.006;
		return 1.0 - smoothstep( rad - w, rad + w, r );

	} else if ( shape < 5.5 ) {

		// PUFF — union of five lobes. A cloud silhouette, not a soft sprite.
		float m = 0.0;
		for ( int i = 0; i < 5; i ++ ) {
			float fi = float( i );
			float ang = fi * 1.2566371 + seed * 6.2831853;
			float wob = fract( seed * ( fi + 3.11 ) );
			vec2 c = vec2( cos( ang ), sin( ang ) ) * ( 0.30 + 0.12 * wob );
			float rr = 0.32 + 0.14 * fract( seed * ( fi * 2.0 + 1.73 ) ) + grow;
			float d = length( p - c );
			float w = fwidth( d ) + 0.006;
			m = max( m, 1.0 - smoothstep( rr - w, rr + w, d ) );
		}
		return m;

	} else if ( shape < 6.5 ) {

		// DOT — hard square. Screen-tone grit.
		vec2 q = abs( p );
		float d = max( q.x, q.y );
		float w = fwidth( d ) + 0.012;
		float edge = 0.58 + grow;
		return 1.0 - smoothstep( edge - w, edge + w, d );

	}

	// SHARD — triangle from three half planes.
	float d = max( max( - p.y - 0.52, p.x * 0.866 + p.y * 0.5 - 0.52 ), - p.x * 0.866 + p.y * 0.5 - 0.52 );
	float w = fwidth( d ) + 0.008;
	return 1.0 - smoothstep( - grow - w, - grow + w, d );

}

void main() {

	// Derivatives are taken before any discard, so they are always well defined.
	float m = shapeMask( vShape, vP, vSeed, 0.0 );
	vec3 col = vCol;

	#ifdef VFX_OUTLINE

		float outer = shapeMask( vShape, vP, vSeed, vGrow );
		if ( outer <= 0.002 ) discard;
		col = mix( uInk, vCol, m );
		m = outer;

	#else

		if ( m <= 0.002 ) discard;

	#endif

	// Dust is screen-toned: a stable, screen-locked halftone grid punched through the
	// silhouette. Locked to gl_FragCoord so it cannot crawl when the camera moves.
	//
	// The PITCH is the thing to be careful with, and it is the one high-frequency pattern
	// this system draws. At a five-pixel pitch the grid is close enough to the sampling
	// rate that a puff moving across the frame twinkles: the silhouette slides over a
	// static grid, so every dot flickers in and out at the edge on its own schedule.
	// Seven pixels with a shallower punch keeps the screen-tone read — it is still
	// obviously a printed dot pattern and not a soft sprite — while putting the pattern
	// comfortably above the frequency where the edge can alias against it.
	if ( vShape > 4.5 && vShape < 5.5 ) {

		vec2 sp = gl_FragCoord.xy / max( uPixelRatio, 0.5 );
		vec2 cell = fract( sp * ( 1.0 / 7.0 ) ) - 0.5;
		float d = length( cell );
		float w = fwidth( d ) + 0.02;
		float tone = 1.0 - smoothstep( 0.32 - w, 0.32 + w, d );
		m *= mix( 0.70, 1.0, tone );

	}

	float a = m * vAlpha;

	#ifdef VFX_ADDITIVE

		// Additive particles ATTENUATE with distance rather than tinting toward the fog.
		// Mixing an additive fragment toward a bright sky-blue fog colour would make a
		// far-off spark *brighter and bluer* than a near one — fog is an occlusion model
		// and additive light is not occluded, it is simply diluted.
		col *= 1.0 - vFog;
		gl_FragColor = vec4( col * a, vBloom * a * ( 1.0 - vFog ) );

	#else

		col = mix( col, uFogColor, vFog );
		gl_FragColor = vec4( col * a, a );

	#endif

}
`;

// ---------------------------------------------------------------------------
// Spawn description
// ---------------------------------------------------------------------------

/**
 * A burst request. Never construct one of these — call {@link ParticleSystem.begin},
 * fill the fields you care about and call {@link ParticleSystem.emit}. The system owns
 * exactly one of these forever, so emitting allocates nothing.
 */
export interface ParticleDesc {
  shape: number;
  /** How many particles this burst spawns. */
  count: number;

  px: number;
  py: number;
  pz: number;
  /** Radius of a uniform random offset applied to the spawn point. */
  posJitter: number;

  vx: number;
  vy: number;
  vz: number;
  /** Half-angle, radians, of a random cone around the base velocity. */
  spread: number;
  /** Base speed is scaled by `1 +/- speedVar`. */
  speedVar: number;
  /** Radius of an additional random velocity offset, m/s. */
  velJitter: number;

  /** Signed vertical acceleration, m/s^2. Negative falls, positive rises. */
  gravity: number;
  /** Linear drag coefficient, 1/s. Clamped to >= 0.02 in the shader. */
  drag: number;

  life: number;
  /** Life is scaled by `1 +/- lifeVar`. */
  lifeVar: number;
  /** Seconds before the particle appears. Staggers a burst into a wave. */
  delay: number;
  delayVar: number;

  size0: number;
  size1: number;
  /** Both sizes are scaled by `1 +/- sizeVar`. */
  sizeVar: number;

  color0: number;
  color1: number;
  intensity0: number;
  intensity1: number;
  /** 0..1 selective-bloom request. Ignored by the matte batch. */
  bloom: number;

  spin: number;
  spinRate: number;
  spinVar: number;
  /** > 0 aligns the quad to velocity and stretches it with speed. */
  stretch: number;

  /** True routes to the additive (light) batch, false to the matte (ink) batch. */
  additive: boolean;
  /** 0 ambient .. 3 critical. Decides who gets evicted when the pool is full. */
  priority: number;
}

function resetDesc(d: ParticleDesc): void {
  d.shape = P_INK;
  d.count = 1;
  d.px = 0;
  d.py = 0;
  d.pz = 0;
  d.posJitter = 0;
  d.vx = 0;
  d.vy = 0;
  d.vz = 0;
  d.spread = 0;
  d.speedVar = 0;
  d.velJitter = 0;
  d.gravity = 0;
  d.drag = 1.2;
  d.life = 0.5;
  d.lifeVar = 0.2;
  d.delay = 0;
  d.delayVar = 0;
  d.size0 = 0.2;
  d.size1 = 0.2;
  d.sizeVar = 0.25;
  // Defaults are INK, not white. An effect that forgets to set a colour should show up
  // as an obvious dark shape on the white world, never vanish into it.
  d.color0 = VFX.ink;
  d.color1 = VFX.ink;
  d.intensity0 = 1;
  d.intensity1 = 1;
  d.bloom = 0;
  d.spin = 0;
  d.spinRate = 0;
  d.spinVar = 0;
  d.stretch = 0;
  d.additive = true;
  d.priority = 1;
}

// ---------------------------------------------------------------------------
// One batch (= one blend mode = one draw call)
// ---------------------------------------------------------------------------

const FLOATS = 4;
/** How many slots to sample when the pool is full and something has to be evicted. */
const EVICT_WINDOW = 48;

/**
 * Matte outline weight in device pixels. An elegant hairline (DESIGN.md 4), not a
 * marker pen — but thick enough that a chip of debris is still a *drawn* shape and not
 * a grey smudge when it is twenty metres away on a white floor.
 */
const OUTLINE_PIXELS = 1.6;

/** Fallback until the first frame tells us the real drawing-buffer height. */
const DEFAULT_VIEWPORT_H = 1080;

const _drawSize = new THREE.Vector2();

class ParticleBatch {
  readonly mesh: THREE.Mesh;
  readonly capacity: number;

  private readonly geometry: THREE.InstancedBufferGeometry;
  private readonly material: THREE.ShaderMaterial;

  private readonly aP0: THREE.InstancedBufferAttribute;
  private readonly aV0: THREE.InstancedBufferAttribute;
  private readonly aSD: THREE.InstancedBufferAttribute;
  private readonly aC0: THREE.InstancedBufferAttribute;
  private readonly aC1: THREE.InstancedBufferAttribute;
  private readonly aX: THREE.InstancedBufferAttribute;

  private readonly dP0: Float32Array;
  private readonly dV0: Float32Array;
  private readonly dSD: Float32Array;
  private readonly dC0: Float32Array;
  private readonly dC1: Float32Array;
  private readonly dX: Float32Array;

  private readonly death: Float32Array;
  private readonly born: Float32Array;
  private readonly prio: Uint8Array;
  private readonly attrs: THREE.InstancedBufferAttribute[];

  private active = 0;
  private evictCursor = 0;
  private dirtyLo = Number.MAX_SAFE_INTEGER;
  private dirtyHi = -1;

  constructor(capacity: number, additive: boolean, renderOrder: number) {
    this.capacity = capacity;

    this.dP0 = new Float32Array(capacity * FLOATS);
    this.dV0 = new Float32Array(capacity * FLOATS);
    this.dSD = new Float32Array(capacity * FLOATS);
    this.dC0 = new Float32Array(capacity * FLOATS);
    this.dC1 = new Float32Array(capacity * FLOATS);
    this.dX = new Float32Array(capacity * FLOATS);
    this.death = new Float32Array(capacity);
    this.born = new Float32Array(capacity);
    this.prio = new Uint8Array(capacity);

    const geo = new THREE.InstancedBufferGeometry();
    geo.name = additive ? 'vfx-particles-add' : 'vfx-particles-ink';
    // Unit quad, corners at +/-0.5. The vertex shader expands it.
    geo.setAttribute(
      'position',
      new THREE.Float32BufferAttribute(
        new Float32Array([-0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0]),
        3,
      ),
    );
    geo.setIndex(new THREE.Uint16BufferAttribute(new Uint16Array([0, 1, 2, 0, 2, 3]), 1));

    const inst = (data: Float32Array): THREE.InstancedBufferAttribute => {
      const a = new THREE.InstancedBufferAttribute(data, FLOATS);
      a.setUsage(THREE.DynamicDrawUsage);
      return a;
    };
    this.aP0 = inst(this.dP0);
    this.aV0 = inst(this.dV0);
    this.aSD = inst(this.dSD);
    this.aC0 = inst(this.dC0);
    this.aC1 = inst(this.dC1);
    this.aX = inst(this.dX);
    geo.setAttribute('aP0', this.aP0);
    geo.setAttribute('aV0', this.aV0);
    geo.setAttribute('aSD', this.aSD);
    geo.setAttribute('aC0', this.aC0);
    geo.setAttribute('aC1', this.aC1);
    geo.setAttribute('aX', this.aX);
    this.attrs = [this.aP0, this.aV0, this.aSD, this.aC0, this.aC1, this.aX];
    geo.instanceCount = 0;
    // Particles live entirely in the shader, so no CPU-side bounds are meaningful.
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);

    const defines: Record<string, string> = {};
    if (additive) defines.VFX_ADDITIVE = '';
    else defines.VFX_OUTLINE = '';

    const mat = new THREE.ShaderMaterial({
      name: additive ? 'vfx-particle-add' : 'vfx-particle-ink',
      defines,
      uniforms: {
        uTime: { value: 0 },
        uInk: { value: new THREE.Color().setHex(PALETTE.ink, THREE.SRGBColorSpace) },
        uFogColor: { value: new THREE.Color(0, 0, 0) },
        uFogNear: { value: 1e6 },
        uFogFar: { value: 1e6 + 1 },
        uViewportH: { value: DEFAULT_VIEWPORT_H },
        uOutlinePx: { value: additive ? 0 : OUTLINE_PIXELS },
        uPixelRatio: { value: 1 },
      },
      vertexShader: PARTICLE_VERT,
      fragmentShader: PARTICLE_FRAG,
      side: THREE.DoubleSide,
      depthTest: true,
      toneMapped: false,
    });
    applyVfxBlend(mat, additive ? 'add' : 'over');

    this.geometry = geo;
    this.material = mat;

    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.name = geo.name;
    this.mesh.frustumCulled = false;
    this.mesh.matrixAutoUpdate = false;
    this.mesh.castShadow = false;
    this.mesh.receiveShadow = false;
    this.mesh.renderOrder = renderOrder;
    // The outline is measured in device pixels, so the shader needs the real
    // drawing-buffer height. Reading it here costs nothing and survives every resize,
    // DPR change and render-target swap without the system needing a renderer handle.
    this.mesh.onBeforeRender = (renderer): void => {
      renderer.getDrawingBufferSize(_drawSize);
      mat.uniforms.uViewportH.value = _drawSize.y > 1 ? _drawSize.y : DEFAULT_VIEWPORT_H;
    };
  }

  get liveCount(): number {
    return this.active;
  }

  get uniforms(): { [k: string]: THREE.IUniform } {
    return this.material.uniforms;
  }

  /** Reserve a slot, evicting the least important particle if the pool is saturated. */
  claimSlot(priority: number): number {
    if (this.active < this.capacity) return this.active++;

    // Full. Sample a rotating window and evict the least important, oldest candidate.
    let victim = -1;
    let victimPrio = 255;
    let victimBorn = Number.MAX_VALUE;
    for (let n = 0; n < EVICT_WINDOW; n++) {
      const i = this.evictCursor;
      this.evictCursor = this.evictCursor + 1 >= this.capacity ? 0 : this.evictCursor + 1;
      const pr = this.prio[i];
      if (pr < victimPrio || (pr === victimPrio && this.born[i] < victimBorn)) {
        victim = i;
        victimPrio = pr;
        victimBorn = this.born[i];
      }
    }
    // Never evict something more important than the incoming particle: a wall of dust
    // must not be able to push a headshot burst off the screen.
    if (victimPrio > priority) return -1;
    return victim;
  }

  private markDirty(i: number): void {
    if (i < this.dirtyLo) this.dirtyLo = i;
    if (i > this.dirtyHi) this.dirtyHi = i;
  }

  write(
    slot: number,
    now: number,
    d: ParticleDesc,
    px: number,
    py: number,
    pz: number,
    vx: number,
    vy: number,
    vz: number,
    life: number,
    delay: number,
    size0: number,
    size1: number,
    spin: number,
    seed: number,
  ): void {
    const b = slot * FLOATS;
    const birth = now + delay;

    this.dP0[b] = px;
    this.dP0[b + 1] = py;
    this.dP0[b + 2] = pz;
    this.dP0[b + 3] = birth;

    this.dV0[b] = vx;
    this.dV0[b + 1] = vy;
    this.dV0[b + 2] = vz;
    this.dV0[b + 3] = life;

    this.dSD[b] = size0;
    this.dSD[b + 1] = size1;
    this.dSD[b + 2] = d.drag;
    this.dSD[b + 3] = d.gravity;

    writeColor(this.dC0, b, d.color0, d.intensity0);
    this.dC0[b + 3] = spin;

    writeColor(this.dC1, b, d.color1, d.intensity1);
    this.dC1[b + 3] = d.spinRate;

    this.dX[b] = d.shape;
    this.dX[b + 1] = seed;
    this.dX[b + 2] = d.bloom;
    this.dX[b + 3] = d.stretch;

    this.born[slot] = birth;
    this.death[slot] = birth + life;
    this.prio[slot] = d.priority;

    this.markDirty(slot);
  }

  private copySlot(from: number, to: number): void {
    const a = from * FLOATS;
    const b = to * FLOATS;
    for (let i = 0; i < FLOATS; i++) {
      this.dP0[b + i] = this.dP0[a + i];
      this.dV0[b + i] = this.dV0[a + i];
      this.dSD[b + i] = this.dSD[a + i];
      this.dC0[b + i] = this.dC0[a + i];
      this.dC1[b + i] = this.dC1[a + i];
      this.dX[b + i] = this.dX[a + i];
    }
    this.death[to] = this.death[from];
    this.born[to] = this.born[from];
    this.prio[to] = this.prio[from];
  }

  update(now: number): void {
    // Reclaim expired slots by swapping the tail down. Keeps the live set contiguous so
    // `instanceCount` is exactly the number of particles the GPU has to look at.
    let i = 0;
    while (i < this.active) {
      if (this.death[i] <= now) {
        this.active--;
        if (i !== this.active) {
          this.copySlot(this.active, i);
          this.markDirty(i);
        }
      } else {
        i++;
      }
    }

    this.geometry.instanceCount = this.active;
    this.material.uniforms.uTime.value = now;

    if (this.dirtyHi >= this.dirtyLo) {
      const lo = this.dirtyLo;
      const count = this.dirtyHi - lo + 1;
      const attrs = this.attrs;
      for (let a = 0; a < attrs.length; a++) {
        const attr = attrs[a];
        attr.clearUpdateRanges();
        attr.addUpdateRange(lo * FLOATS, count * FLOATS);
        attr.needsUpdate = true;
      }
      this.dirtyLo = Number.MAX_SAFE_INTEGER;
      this.dirtyHi = -1;
    }
  }

  clear(): void {
    this.active = 0;
    this.geometry.instanceCount = 0;
    this.dirtyLo = Number.MAX_SAFE_INTEGER;
    this.dirtyHi = -1;
  }

  dispose(): void {
    this.mesh.onBeforeRender = (): void => {};
    this.geometry.dispose();
    this.material.dispose();
  }
}

// ---------------------------------------------------------------------------
// The system
// ---------------------------------------------------------------------------

export interface ParticleSystemOptions {
  /** Hard cap on simultaneous additive (light) particles. */
  additiveCapacity?: number;
  /** Hard cap on simultaneous matte (ink) particles. */
  matteCapacity?: number;
  /** Render order for the additive batch. Ink draws one below it. */
  renderOrder?: number;
}

/**
 * Matte gets the bigger pool, because on a white world matte is what an effect is
 * mostly made of and additive is only its highlight. (This is the reverse of the split
 * a dark game would want, and getting it backwards is why bright-world VFX usually
 * disappear.)
 */
/** Push one linear fog (or none) into a batch's uniforms. Allocation-free. */
function applyFog(u: { [k: string]: THREE.IUniform }, fog: THREE.Fog | null): void {
  if (fog !== null) {
    (u.uFogColor.value as THREE.Color).copy(fog.color);
    u.uFogNear.value = fog.near;
    u.uFogFar.value = fog.far;
  } else {
    u.uFogNear.value = 1e6;
    u.uFogFar.value = 1e6 + 1;
  }
}

const DEFAULT_ADD_CAP = 3000;
const DEFAULT_INK_CAP = 5000;
const DEFAULT_RENDER_ORDER = 12;

export class ParticleSystem {
  /** The scratch burst description. Owned by the system; never hold a reference to it. */
  readonly desc: ParticleDesc;

  private readonly scene: THREE.Scene;
  private readonly add: ParticleBatch;
  private readonly ink: ParticleBatch;
  private readonly rng: () => number;
  private now = 0;
  private disposed = false;

  constructor(scene: THREE.Scene, opts: ParticleSystemOptions = {}) {
    this.scene = scene;
    const order = opts.renderOrder ?? DEFAULT_RENDER_ORDER;
    this.add = new ParticleBatch(opts.additiveCapacity ?? DEFAULT_ADD_CAP, true, order);
    this.ink = new ParticleBatch(opts.matteCapacity ?? DEFAULT_INK_CAP, false, order - 1);
    scene.add(this.ink.mesh);
    scene.add(this.add.mesh);

    this.rng = makeRng(0x51ce7a11);

    this.desc = {} as ParticleDesc;
    resetDesc(this.desc);

    this.setPixelRatio(typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1);
  }

  /** Seconds since the system was created. Effects that need their own clock share this one. */
  get time(): number {
    return this.now;
  }

  get liveAdditive(): number {
    return this.add.liveCount;
  }

  get liveMatte(): number {
    return this.ink.liveCount;
  }

  /** Random in [0,1). Shared deterministic stream, so the look is stable across sessions. */
  random(): number {
    return this.rng();
  }

  /** Reset the scratch description to defaults and hand it back for filling. */
  begin(shape: number, additive: boolean): ParticleDesc {
    resetDesc(this.desc);
    this.desc.shape = shape;
    this.desc.additive = additive;
    return this.desc;
  }

  /** Spawn the burst currently described by {@link desc}. Allocates nothing. */
  emit(): void {
    if (this.disposed) return;
    const d = this.desc;
    const batch = d.additive ? this.add : this.ink;
    const n = d.count | 0;
    if (n <= 0) return;

    const rng = this.rng;
    const now = this.now;

    // Orthonormal frame around the base velocity, for cone spread.
    let bx = d.vx;
    let by = d.vy;
    let bz = d.vz;
    let speed = Math.sqrt(bx * bx + by * by + bz * bz);
    let ux = 0;
    let uy = 0;
    let uz = 0;
    let wx = 0;
    let wy = 0;
    let wz = 0;
    if (speed > 1e-5) {
      bx /= speed;
      by /= speed;
      bz /= speed;
      // Pick the world axis least aligned with the direction to avoid a degenerate cross.
      const ax = Math.abs(bx);
      const ay = Math.abs(by);
      const az = Math.abs(bz);
      let hx = 0;
      let hy = 0;
      let hz = 0;
      if (ax <= ay && ax <= az) hx = 1;
      else if (ay <= az) hy = 1;
      else hz = 1;
      ux = by * hz - bz * hy;
      uy = bz * hx - bx * hz;
      uz = bx * hy - by * hx;
      const ul = Math.hypot(ux, uy, uz) || 1;
      ux /= ul;
      uy /= ul;
      uz /= ul;
      wx = by * uz - bz * uy;
      wy = bz * ux - bx * uz;
      wz = bx * uy - by * ux;
    } else {
      speed = 0;
    }

    for (let i = 0; i < n; i++) {
      const slot = batch.claimSlot(d.priority);
      if (slot < 0) continue;

      // --- position
      let px = d.px;
      let py = d.py;
      let pz = d.pz;
      if (d.posJitter > 0) {
        const r = d.posJitter;
        px += (rng() * 2 - 1) * r;
        py += (rng() * 2 - 1) * r;
        pz += (rng() * 2 - 1) * r;
      }

      // --- velocity
      let vx = d.vx;
      let vy = d.vy;
      let vz = d.vz;
      if (speed > 0) {
        let dx = bx;
        let dy = by;
        let dz = bz;
        if (d.spread > 0) {
          const cosMax = Math.cos(d.spread);
          const ct = cosMax + (1 - cosMax) * rng();
          const st = Math.sqrt(Math.max(0, 1 - ct * ct));
          const phi = rng() * Math.PI * 2;
          const cp = Math.cos(phi) * st;
          const sp = Math.sin(phi) * st;
          dx = bx * ct + ux * cp + wx * sp;
          dy = by * ct + uy * cp + wy * sp;
          dz = bz * ct + uz * cp + wz * sp;
        }
        const s = speed * (1 + (rng() * 2 - 1) * d.speedVar);
        vx = dx * s;
        vy = dy * s;
        vz = dz * s;
      }
      if (d.velJitter > 0) {
        const r = d.velJitter;
        vx += (rng() * 2 - 1) * r;
        vy += (rng() * 2 - 1) * r;
        vz += (rng() * 2 - 1) * r;
      }

      const life = Math.max(0.02, d.life * (1 + (rng() * 2 - 1) * d.lifeVar));
      const delay = Math.max(0, d.delay + rng() * d.delayVar);
      const sizeScale = 1 + (rng() * 2 - 1) * d.sizeVar;
      const spin = d.spin + (rng() * 2 - 1) * d.spinVar;

      batch.write(
        slot,
        now,
        d,
        px,
        py,
        pz,
        vx,
        vy,
        vz,
        life,
        delay,
        d.size0 * sizeScale,
        d.size1 * sizeScale,
        spin,
        rng(),
      );
    }
  }

  /** Keeps the screen-tone halftone pitch constant in physical pixels. */
  setPixelRatio(v: number): void {
    const r = v > 0 ? v : 1;
    this.add.uniforms.uPixelRatio.value = r;
    this.ink.uniforms.uPixelRatio.value = r;
  }

  /**
   * Match both batches to the scene's distance fog, so far-off debris never floats out
   * of the aerial perspective the rest of the world obeys. The two batches consume it
   * differently — matte tints toward the fog colour, additive simply dims (see the
   * fragment shader) — which is why the additive pool needs the range too and not just
   * the colour.
   */
  syncFog(fog: THREE.Fog | THREE.FogExp2 | null): void {
    const linear =
      fog !== null && (fog as THREE.Fog).isFog === true ? (fog as THREE.Fog) : null;
    // Written out rather than looped over an array literal: this runs every frame, and
    // this system does not allocate during a match.
    applyFog(this.ink.uniforms, linear);
    applyFog(this.add.uniforms, linear);
  }

  update(dtSec: number): void {
    if (this.disposed) return;
    const dt = dtSec > 0.25 ? 0.25 : dtSec > 0 ? dtSec : 0;
    this.now += dt;
    this.add.update(this.now);
    this.ink.update(this.now);
  }

  /** Drop every live particle instantly. Used on round transitions. */
  clear(): void {
    this.add.clear();
    this.ink.clear();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.scene.remove(this.add.mesh);
    this.scene.remove(this.ink.mesh);
    this.add.dispose();
    this.ink.dispose();
  }
}
